import { throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { logarithmicTime } from 'src/app/@utils/logarithmic-time.helper';

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NewsletterService } from '@core/services/newsletter.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { RelewiseTrackingService } from '@core/services/relewise/relewise-tracking.service';
import { NewsletterSpot } from '@features/spots/umbraco-block-list-spot/block-list-spot.models';
import { StoresService } from '@features/stores-overview/service/stores.service';
import { NewsletterSignUp, StoreViewModel } from '@models/api.models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CustomValidatorsService } from '@shared/form-elements/custom-validators/custom-validators.service';
import { FormModelControls } from '@shared/form-elements/model/form-model-controls';
import { TranslationKey } from '@shared/translations/auto-generated/translation-key';
import { TranslateSafePipe } from '../../../@shared/translate-safe/translate-safe.pipe';
import { SafePipe } from '@shared/safe/safe.pipe';
import { LoadingDotsComponent } from '../../../@shared/loading-dots/loading-dots.component';
import { InterceptLinksDirective } from '../../../@libs/routing/dynamic-routing/intercept-links.directive';
import { CheckboxComponent } from '../../../@shared/form-elements/checkbox/checkbox.component';
import { InputEmailComponent } from '../../../@shared/form-elements/input/input-email/input-email.component';
import { InputComponent } from '../../../@shared/form-elements/input/input/input.component';
import { HandleInvalidOnSubmitDirective } from '../../../@shared/form-elements/form-helpers/handle-invalid-on-submit.directive';
import { ArrowLinkComponent } from '../../../@shared/arrow-link/arrow-link.component';
import { SpotHeadlineComponent } from '../spot-headline/spot-headline.component';
import { NgIf } from '@angular/common';

type FormModel = {
    email: string;
    firstName: string;
    lastName: string;
    isOptIn: boolean;
    store?: StoreViewModel;
};
@UntilDestroy()
@Component({
    selector: 'app-newsletter',
    templateUrl: './newsletter.component.html',
    styleUrls: ['./newsletter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, SpotHeadlineComponent, ArrowLinkComponent, FormsModule, HandleInvalidOnSubmitDirective, ReactiveFormsModule, InputComponent, InputEmailComponent, CheckboxComponent, InterceptLinksDirective, LoadingDotsComponent, SafePipe, TranslateSafePipe]
})
export class NewsletterComponent implements OnInit {
    public static readonly ref: string = 'newsletter';

    public form: UntypedFormGroup;
    public stores: StoreViewModel[] = [];
    public submitting: boolean = false;

    @Input()
    public data: NewsletterSpot;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly customValidatorsService: CustomValidatorsService,
        private readonly cd: ChangeDetectorRef,
        private readonly newsletterService: NewsletterService,
        private readonly notificationService: NotificationService,
        private readonly relewiseTrackingService: RelewiseTrackingService,
        public readonly storesService: StoresService
    ) {}

    ngOnInit(): void {
        this.storesService.allStores$.pipe(untilDestroyed(this)).subscribe(stores => {
            this.stores = stores;
            this.cd.markForCheck();
        });

        const formControls: FormModelControls<FormModel> = {
            email: ['', [Validators.required, this.customValidatorsService.email]],
            firstName: ['', Validators.required],
            lastName: [''],
            isOptIn: [false, Validators.requiredTrue],
            store: ['']
        };
        this.form = this.formBuilder.group(formControls);
    }

    public onSubmit(): void {
        if (!this.form.valid) {
            const invalidControls = Object.keys(this.form.controls).filter(
                controlName => this.form.controls[controlName].invalid
            );
            this.notificationService.createNotification({
                translationHeadline: 'newsletter.validation.missingHeader',
                translationArray: invalidControls.map(
                    controlName => `newsletter.validation.missing.${controlName}`
                ) as TranslationKey[],
                fadeTime: logarithmicTime(invalidControls.length, 5000)
            });
            return;
        }
        this.submitting = true;
        const formData = this.form.getRawValue() as FormModel;

        this.newsletterService
            .subscribeToNewsletter({
                ...formData,
                store: formData?.store?.storeId || ''
            } as NewsletterSignUp)
            .pipe(
                take(1),
                catchError(error => throwError(error))
            )
            .subscribe(
                successRes => {
                    this.submitting = false;
                    this.cd.markForCheck();
                    this.notificationService.createNotification({
                        type: 'success',
                        ...(successRes && successRes.length
                            ? { messageArray: successRes }
                            : { translationText: 'newsletter.validation.success' }),
                        fadeTime: logarithmicTime(successRes.length, 8000)
                    });
                    this.relewiseTrackingService.trackUser(formData.email);
                    this.form.reset();
                    this.form.patchValue({
                        email: '',
                        firstName: '',
                        lastName: '',
                        isOptIn: false,
                        store: ''
                    });
                },
                errorRes => {
                    // We can get error as string, as string[] and as message, so we must do some mapping
                    const errorArray = errorRes.error?.length
                        ? typeof errorRes.error === 'string'
                            ? [errorRes.error]
                            : errorRes.error
                        : errorRes.message
                        ? [errorRes.message]
                        : '';

                    this.submitting = false;
                    this.cd.markForCheck();
                    this.notificationService.createNotification({
                        type: 'error',
                        fadeTime: logarithmicTime(errorArray.length, 8000),
                        ...(errorRes.error
                            ? { messageArray: errorArray }
                            : { translationText: 'newsletter.validation.error' })
                    });
                }
            );
    }
}
